import type { Classifier } from '@zg/shared/property';

/**
 * Lightweight sessionStorage wrapper for the Property Insurance Quote flow.
 * Keys are scoped with a `zg-property:` prefix so they don't collide with
 * the BR indication flow's storage.
 *
 * Draft persistence (magic-link / DynamoDB) is wired in P-6; this is the
 * in-session fallback for the pre-draft stretch between classifier and
 * first section blur.
 */

const PREFIX = 'zg-property:';
const CLASSIFIER_KEY = `${PREFIX}classifier`;
const ANSWERS_KEY = `${PREFIX}answers`;
const DRAFT_ID_KEY = `${PREFIX}draftId`;

function hasWindow(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

export function saveClassifier(classifier: Classifier): void {
  if (!hasWindow()) return;
  try {
    window.sessionStorage.setItem(CLASSIFIER_KEY, JSON.stringify(classifier));
  } catch {
    // sessionStorage can throw in private browsing modes; fail soft
  }
}

export function loadClassifier(): Classifier | null {
  if (!hasWindow()) return null;
  try {
    const raw = window.sessionStorage.getItem(CLASSIFIER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Classifier;
  } catch {
    return null;
  }
}

export function clearClassifier(): void {
  if (!hasWindow()) return;
  try {
    window.sessionStorage.removeItem(CLASSIFIER_KEY);
  } catch {
    // noop
  }
}

export function saveAnswers(answers: Record<string, unknown>): void {
  if (!hasWindow()) return;
  try {
    window.sessionStorage.setItem(ANSWERS_KEY, JSON.stringify(answers));
  } catch {
    // noop
  }
}

export function loadAnswers(): Record<string, unknown> {
  if (!hasWindow()) return {};
  try {
    const raw = window.sessionStorage.getItem(ANSWERS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function clearAnswers(): void {
  if (!hasWindow()) return;
  try {
    window.sessionStorage.removeItem(ANSWERS_KEY);
  } catch {
    // noop
  }
}

export function saveDraftId(id: string): void {
  if (!hasWindow()) return;
  try {
    window.sessionStorage.setItem(DRAFT_ID_KEY, id);
  } catch {
    // noop
  }
}

export function loadDraftId(): string | null {
  if (!hasWindow()) return null;
  try {
    return window.sessionStorage.getItem(DRAFT_ID_KEY);
  } catch {
    return null;
  }
}

export function clearDraftId(): void {
  if (!hasWindow()) return;
  try {
    window.sessionStorage.removeItem(DRAFT_ID_KEY);
  } catch {
    // noop
  }
}

export function clearAll(): void {
  clearClassifier();
  clearAnswers();
  if (!hasWindow()) return;
  try {
    window.sessionStorage.removeItem(DRAFT_ID_KEY);
  } catch {
    // noop
  }
}
